import type { ErrorType } from "../hooks/useApi";

/**
 * O que o app faz além de mostrar a mensagem:
 * - retry: o aviso ganha "Tentar novamente" (quando quem chamou sabe repetir);
 * - relogin: leva ao login do Auth0;
 * - upgrade: abre o UpgradeModal em vez do aviso.
 */
export type ErrorAction = "retry" | "relogin" | "upgrade";

export interface ErrorMessage {
  /** O que aconteceu, em poucas palavras. */
  title: string;
  /** O que foi preservado e o que fazer. O `detail` do backend, quando existe, tem prioridade. */
  body: string;
  action?: ErrorAction;
}

const TRY_AGAIN = "Seus dados continuam aqui. Tente de novo em alguns segundos.";
const SAVE_FAILED = "O que você preencheu continua na tela. Tente salvar de novo em alguns segundos.";
const SUPPORT = "Se continuar, fale com o suporte informando o código abaixo.";

// Record: um ErrorType novo sem mensagem não compila.
export const ERROR_MESSAGES: Record<ErrorType, ErrorMessage> = {
  // ── Gerados no próprio front ──────────────────────────────────────────────
  "network-error": {
    title: "Você está sem internet",
    body: "Pode continuar editando. Quando a conexão voltar, tente de novo.",
    action: "retry",
  },
  "server-unreachable": {
    title: "Não conseguimos falar com o servidor",
    body: `Sua internet está funcionando, mas o servidor não respondeu. ${TRY_AGAIN}`,
    action: "retry",
  },
  timeout: {
    title: "O servidor demorou demais para responder",
    body: TRY_AGAIN,
    action: "retry",
  },
  "stream-interrupted": {
    title: "A resposta da IA veio incompleta",
    body: "Seus dados estão salvos. Gere o pedido de novo; se continuar, fale com o suporte.",
    action: "retry",
  },
  unexpected: {
    title: "Algo deu errado nesta tela",
    body: "Seus dados salvos não foram perdidos. Recarregue a página e tente de novo.",
  },
  "file-type-not-allowed": {
    title: "Tipo de arquivo não aceito",
    body: "Envie um arquivo em PDF.",
  },
  "file-too-large": {
    title: "Arquivo grande demais",
    body: "O limite é 10 MB por arquivo. Reduza o arquivo ou envie outro.",
  },
  "file-duplicated": {
    title: "Esse arquivo já foi adicionado",
    body: "Ele já está na lista de anexos.",
  },
  "mic-permission-denied": {
    title: "Sem acesso ao microfone",
    body: "Libere o microfone nas permissões do navegador e tente gravar de novo.",
  },
  "clipboard-failed": {
    title: "Não foi possível copiar",
    body: "Selecione o texto e copie manualmente.",
  },
  unknown: {
    title: "O servidor não respondeu como esperado",
    body: TRY_AGAIN,
    action: "retry",
  },

  // ── Genéricos do backend ──────────────────────────────────────────────────
  "internal-server-error": { title: "Erro no servidor", body: `${TRY_AGAIN} ${SUPPORT}`, action: "retry" },
  "invalid-payload": { title: "Alguns dados não estão válidos", body: "Confira os campos e tente de novo." },
  "not-found": { title: "Não encontramos o que você procurou", body: "Pode ter sido removido. Volte e tente de novo." },
  unauthorized: { title: "Sua sessão expirou", body: "Entre de novo para continuar. O que já foi salvo não se perde.", action: "relogin" },
  forbidden: { title: "Você não tem permissão para isso", body: "Se acha que deveria ter, fale com o responsável pela sua conta." },
  conflict: { title: "Esses dados mudaram enquanto você editava", body: "Recarregue a página para ver a versão atual." },

  // ── Domínio ───────────────────────────────────────────────────────────────
  "pdf-generation-failed": { title: "Não conseguimos gerar o PDF", body: `${TRY_AGAIN} ${SUPPORT}`, action: "retry" },
  "s3-upload-failed": { title: "Não conseguimos enviar o arquivo", body: "Tente enviar de novo em alguns segundos.", action: "retry" },
  "s3-delete-failed": { title: "Não conseguimos remover o arquivo", body: "Tente de novo em alguns segundos.", action: "retry" },
  "s3-list-failed": { title: "Não conseguimos carregar seus arquivos", body: TRY_AGAIN, action: "retry" },
  "ocr-failed": { title: "Não conseguimos ler o documento", body: "Tente uma foto mais nítida ou preencha os dados à mão." },
  "rag-chain-not-found": { title: "A sugestão de procedimentos expirou", body: "Volte ao novo pedido e gere as sugestões de novo." },
  "dynamodb-write-failed": { title: "Não conseguimos salvar", body: SAVE_FAILED, action: "retry" },
  "dynamodb-update-failed": { title: "Não conseguimos salvar as alterações", body: SAVE_FAILED, action: "retry" },
  "dynamodb-read-failed": { title: "Não conseguimos carregar os dados", body: TRY_AGAIN, action: "retry" },
  "dynamodb-delete-failed": { title: "Não conseguimos remover", body: "Nada foi apagado. Tente de novo em alguns segundos.", action: "retry" },
  "db-update-failed": { title: "Não conseguimos salvar as alterações", body: SAVE_FAILED, action: "retry" },
  "db-write-failed": { title: "Não conseguimos salvar", body: SAVE_FAILED, action: "retry" },
  "db-not-found": { title: "Registro não encontrado", body: "Pode ter sido removido. Recarregue a página." },
  "db-connection-failed": { title: "O servidor está instável", body: TRY_AGAIN, action: "retry" },
  "openai-error": { title: "A IA não respondeu", body: "Seus dados estão salvos. Tente gerar de novo em alguns segundos.", action: "retry" },
  "gemini-error": { title: "A IA não respondeu", body: "Seus dados estão salvos. Tente gerar de novo em alguns segundos.", action: "retry" },
  "service-busy": { title: "Muita gente usando agora", body: "Seus dados estão salvos. Tente de novo em alguns segundos.", action: "retry" },
  "email-account-not-connected": { title: "Seu e-mail não está conectado", body: "Conecte sua conta de e-mail em Configurações para enviar por aqui." },
  "email-oauth-failed": { title: "Não conseguimos conectar seu e-mail", body: "Tente conectar de novo. Se continuar, fale com o suporte." },
  "email-send-failed": { title: "O e-mail não foi enviado", body: "A mensagem e os anexos continuam aqui. Tente enviar de novo.", action: "retry" },

  // ── Pagamento e planos ────────────────────────────────────────────────────
  "stripe-error": { title: "O pagamento não respondeu", body: "Nenhuma cobrança foi feita. Tente de novo em alguns minutos.", action: "retry" },
  "stripe-invalid-payload": { title: "Erro no pagamento", body: SUPPORT },
  "stripe-invalid-signature": { title: "Erro no pagamento", body: SUPPORT },
  "plan-update-failed": { title: "Não conseguimos atualizar seu plano", body: `Nenhuma cobrança extra foi feita. ${SUPPORT}` },
  "plano-desconhecido": { title: "Plano inválido", body: "Escolha um plano na página de planos." },
  "lookup-key-desconhecida": { title: "Este preço não está disponível", body: SUPPORT },
  "plano-inativo": { title: "Este plano não está disponível no momento", body: "Escolha outro plano na página de planos." },
  "limite-free-atingido": { title: "Você atingiu o limite do plano gratuito", body: "Assine um plano para continuar gerando pedidos este mês.", action: "upgrade" },
  "assinatura-ja-existe": { title: "Você já tem uma assinatura ativa", body: "Veja os detalhes em Configurações." },
  "prazo-arrependimento-expirado": { title: "O prazo de arrependimento já passou", body: "Você ainda pode cancelar a renovação em Configurações." },
  "pagamento-recusado": { title: "Pagamento recusado", body: "Nenhuma cobrança foi feita. Confira os dados do cartão ou use outro." },
  "pagamento-nao-vinculavel": { title: "Este pagamento não pode ser vinculado", body: SUPPORT },
  "oferta-indisponivel": { title: "Esta oferta não está mais disponível", body: "Veja os planos atuais na página de planos." },
  "oferta-ja-utilizada": { title: "Você já usou esta oferta", body: "Veja os planos atuais na página de planos." },
  "plano-avulso-ativo": { title: "Você tem um plano individual ativo", body: "Ao entrar na clínica, o plano individual é cancelado. Confirme para continuar." },

  // ── Clínica, convites e papéis ────────────────────────────────────────────
  "secretaria-sem-medico": { title: "Nenhum médico vinculado", body: "Peça ao médico para vincular você em Configurações." },
  "papel-sem-permissao": { title: "Seu papel não permite esta ação", body: "Peça ao médico responsável para fazer isso." },
  "convite-invalido": { title: "Convite inválido", body: "O convite expirou ou foi revogado. Peça um novo a quem convidou você." },
  "convite-email-diferente": { title: "Este convite é para outro e-mail", body: "Entre com o e-mail que recebeu o convite." },
  "ja-em-outra-clinica": { title: "Este e-mail já faz parte de outra clínica", body: "Saia da outra clínica antes de entrar nesta." },
};
